import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  SubscriptionsEndpoints as Endpoints,
  type CreateSubscriptionRequest,
  type CreateSubscriptionResponse,
  type DeleteSubscriptionRequest,
  type DeleteSubscriptionResponse,
  type EditSubscriptionRequest,
  type EditSubscriptionResponse,
  type GetClientSubscriptionsRequest,
  type GetClientSubscriptionsResponse,
  type GetRecordSubscriptionsRequest,
  type GetRecordSubscriptionsResponse,
  type GetSubscriptionByIdRequest,
  type GetSubscriptionByIdResponse,
  type GetSubscriptionsForSaleRequest,
  type GetSubscriptionsForSaleResponse,
  type GetSubscriptionsRequest,
  type GetSubscriptionsResponse,
  type SetRecordSubscriptionsRequest,
  type SetRecordSubscriptionsResponse,
} from '@app/api/Subscriptions/types';
import { SUBSCRIPTIONS_CHUNK } from '@app/constants';

const SUBSCRIPTIONS_TAG = 'Subscriptions';
const RECORD_SUBSCRIPTIONS_TAG = 'RecordSubscriptions';
const CLIENT_SUBSCRIPTIONS_TAG = 'ClientSubscriptions';

export const subscriptionsApi = createApi({
  reducerPath: 'subscriptionsApi',
  baseQuery: createApiBaseQuery(SUBSCRIPTIONS_CHUNK),
  tagTypes: [
    SUBSCRIPTIONS_TAG,
    RECORD_SUBSCRIPTIONS_TAG,
    CLIENT_SUBSCRIPTIONS_TAG,
  ],
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetSubscriptions]: query<
      GetSubscriptionsResponse,
      GetSubscriptionsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSubscriptions,
        method: Methods.GET,
        params,
      }),
      providesTags: [SUBSCRIPTIONS_TAG],
    }),
    [Endpoints.GetSubscriptionById]: query<
      GetSubscriptionByIdResponse,
      GetSubscriptionByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSubscriptionById,
        method: Methods.GET,
        params,
      }),
      providesTags: [SUBSCRIPTIONS_TAG],
    }),
    [Endpoints.CreateSubscription]: mutation<
      CreateSubscriptionResponse,
      CreateSubscriptionRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateSubscription,
        method: Methods.POST,
        params,
        data,
      }),
      invalidatesTags: [SUBSCRIPTIONS_TAG],
    }),
    [Endpoints.EditSubscription]: mutation<
      EditSubscriptionResponse,
      EditSubscriptionRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.EditSubscription,
        method: Methods.PUT,
        params,
        data,
      }),
      invalidatesTags: [SUBSCRIPTIONS_TAG],
    }),
    [Endpoints.DeleteSubscription]: mutation<
      DeleteSubscriptionResponse,
      DeleteSubscriptionRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteSubscription,
        method: Methods.DELETE,
        params,
      }),
      invalidatesTags: [SUBSCRIPTIONS_TAG],
    }),
    // Абонементы реестра, доступные для продажи в окне записи. Тег общий с
    // реестром: правка шаблона обновляет и этот список.
    [Endpoints.GetSubscriptionsForSale]: query<
      GetSubscriptionsForSaleResponse,
      GetSubscriptionsForSaleRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSubscriptionsForSale,
        method: Methods.GET,
        params,
      }),
      providesTags: [SUBSCRIPTIONS_TAG],
    }),
    [Endpoints.GetRecordSubscriptions]: query<
      GetRecordSubscriptionsResponse,
      GetRecordSubscriptionsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordSubscriptions,
        method: Methods.GET,
        params,
      }),
      providesTags: [RECORD_SUBSCRIPTIONS_TAG],
    }),
    [Endpoints.SetRecordSubscriptions]: mutation<
      SetRecordSubscriptionsResponse,
      SetRecordSubscriptionsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetRecordSubscriptions,
        method: Methods.POST,
        params,
        data,
      }),
      // Продажа и удаление меняют и абонементы клиента.
      invalidatesTags: [RECORD_SUBSCRIPTIONS_TAG, CLIENT_SUBSCRIPTIONS_TAG],
    }),
    [Endpoints.GetClientSubscriptions]: query<
      GetClientSubscriptionsResponse,
      GetClientSubscriptionsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientSubscriptions,
        method: Methods.GET,
        params,
      }),
      providesTags: [CLIENT_SUBSCRIPTIONS_TAG],
    }),
  }),
});

export const {
  useGetSubscriptionsQuery,
  useGetSubscriptionByIdQuery,
  useLazyGetSubscriptionByIdQuery,
  useCreateSubscriptionMutation,
  useEditSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useGetSubscriptionsForSaleQuery,
  useLazyGetSubscriptionsForSaleQuery,
  useLazyGetRecordSubscriptionsQuery,
  useSetRecordSubscriptionsMutation,
  useGetClientSubscriptionsQuery,
} = subscriptionsApi;

/**
 * Сбросить кэш абонементов клиента. UpdateRecord живёт в другом createApi и
 * сам тег не сбрасывает, а услуги абонементов клиента он занимает и
 * освобождает: вызывайте после успешного UpdateRecord.
 */
export const invalidateClientSubscriptions = () =>
  subscriptionsApi.util.invalidateTags([CLIENT_SUBSCRIPTIONS_TAG]);
